// The shared UI analytics vocabulary: screen, button, field and device taxonomies.
// Pure string enums with no imports — every consumer reads them through this door.

export enum ScreenName {
  SideBySideTrialDropdown = 'Side by Side Trial Dropdown',
  UpgradeToProModal = 'Upgrade to Pro Modal',
  UpgradeToPracticeModal = 'upgrade_to_practice_modal',
  UpgradeToClinicianModal = 'upgrade_to_clinician_modal',
  UpgradeToClinicianMaxModal = 'upgrade_to_clinician_max_modal',
  UpgradeToEvidencePowerUserModal = 'upgrade_to_evidence_power_user_modal',
  OnboardingSignUpForm = 'onboarding_sign_up_form',
  OnboardingCustomizeOrganization = 'onboarding_customize_organization',
  AuthFormSignup = 'auth_form_signup',
  AuthForm = 'auth_form',
  // Onboarding v2 full-page sign-up route
  SignupFullPageView = 'signup_full_page_view',
  /** Early meeting flow entry screen (Sign up free vs Book a Demo) — SLG entry from marketing. */
  SlgFlow = 'slg_flow',
  // Marketing-attributed entry that bypasses the V2 sign-up form and redirects
  // straight to Auth0 (gated by `release-onboarding-signup-bypass`).
  SignupBypass = 'signup_bypass',
  /** Learn-basics flow privacy/get-started screen (final step before completion). */
  OnboardingPrivacy = 'onboarding_privacy',
  /** Learn-basics team-invite step — distinct from post-onboarding `practice_invite_team_modal`. */
  OnboardingInviteTeam = 'onboarding_invite_team',
  /**
   * Guided-tour arm only (`release-new-onboarding-flow` = `experimental-3`): the tour's
   * treatment of the shared mic-test step, plus the three screens the control
   * arm's single `note-edit` accordion is split into. The transcription screen
   * is the tour funnel's first step, so it has to report a view of its own —
   * without it the funnel starts one screen in and every drop-off there is
   * invisible.
   */
  OnboardingTourTranscription = 'onboarding_tour_transcription',
  OnboardingTourDetail = 'onboarding_tour_detail',
  OnboardingTourArtefacts = 'onboarding_tour_artefacts',
  OnboardingTourEvidence = 'onboarding_tour_evidence',
  // Legacy screen names for non-email-only flows
  LoginV1 = 'login_v1',
  SignupV1 = 'signup_v1',
  OnboardingUserSurvey = 'onboarding_user_survey',
  OnboardingOrgSurvey = 'onboarding_org_survey',
  OrganizationSettingsModal = 'organization_settings_modal',
  GettingStartedOptions = 'getting_started_options',
  GetHeidiMobile = 'get_heidi_mobile',
  GetMobilePage = 'get_mobile_page',
  // Deeplink pages for Evidence release
  Evidence = 'evidence',
  EvidenceOverview = 'evidence_overview',
  // Trial Extension
  TrialExtensionModal = 'trial_extension_modal',
  TrialExtensionInviteModal = 'trial_extension_invite_modal',
  TrialExtensionHoverCard = 'trial_extension_hover_card',
  /** One-time celebration tooltip shown when all gamified trial tasks are complete */
  TrialExtensionCompletionTooltip = 'trial_extension_completion_tooltip',
  /** Floating popover when a 90-day fake clinician trial has ~7 days left */
  NinetyDayTrialEndingSoonReminderPopup = 'ninety_day_trial_ending_soon_reminder_popup',
  /** Recap modal opened from the 90-day trial reminder popup's "See summary" CTA */
  NinetyDayTrialRecapModal = 'ninety_day_trial_recap_modal',
  /** HubSpot callback request form (Clinician / Practice upgrade path) */
  TrialUpgradeRequestCallbackModal = 'trial_upgrade_request_callback_modal',
  /** Non-admin team member request form to ask their admin to upgrade */
  TeamTrialUpgradeRequestModal = 'team_trial_upgrade_request_modal',
  /** After Clinician trial ends: usage recap vs Scribe Plus retention before upgrade */
  ClinicianTrialEndedModal = 'clinician_trial_ended_modal',
  /** After Practice trial ends: usage recap vs Scribe Team retention before upgrade */
  PracticeTrialEndedModal = 'practice_trial_ended_modal',
  /** Soft-touch corner card shown to low-usage users after Clinician/Practice trial ends, before the big upgrade modal */
  NinetyDayTrialEndDiscreetPopup = 'ninety_day_trial_end_discreet_popup',
  /** GRO-3976: Staged trial upgrade nudge (sidebar or header icon). */
  TrialUpgradeNudge = 'trial_upgrade_nudge',
  /** GRO-3976: Hover popover for the staged trial upgrade nudge. */
  TrialUpgradeNudgePopover = 'trial_upgrade_nudge_popover',
  /** Post-checkout welcome card with Evidence Loom embed (Clinician / Practice) */
  EvidencePostUpgradeWelcomePopup = 'evidence_post_upgrade_welcome_popup',
  /** Three-step onboarding dialog opened from the post-upgrade welcome CTA */
  EvidencePostUpgradeOnboardingModal = 'evidence_post_upgrade_onboarding_modal',
  // Session
  Session = 'session',
  SessionList = 'session_list',
  /** "Have you reviewed your note?" gate shown before copy/export/push (PRODA-146). */
  ReviewConfirmationPopover = 'review_confirmation_popover',
  /** V1 session-page awareness card inviting the user to opt into the V2 layout. */
  ScribeLayoutV2InviteCard = 'scribe_layout_v2_invite_card',
  /** Permanent compact V1 banner promoting the V2 layout during rollout. */
  ScribeLayoutV2InviteBanner = 'scribe_layout_v2_invite_banner',
  /** Persistent V2 banner that relaunches the layout tour. */
  ScribeLayoutV2TourBanner = 'scribe_layout_v2_tour_banner',
  /** Persistent Heidi II early-access awareness banner. */
  Heidi2AwarenessBanner = 'heidi_2_awareness_banner',
  /** Persistent Heidi II request-access banner for non-admin clinicians. */
  Heidi2RequestAccessBanner = 'heidi_2_request_access_banner',
  /** Heidi II opt-in consent modal opened from the awareness banner. */
  Heidi2OptInModal = 'heidi_2_opt_in_modal',
  /** Heidi II request-access modal where a non-admin asks their workspace admins. */
  Heidi2RequestAccessModal = 'heidi_2_request_access_modal',
  /** Team-admin modal confirming who on the team gets Heidi II access. */
  Heidi2ConfirmAccessModal = 'heidi_2_confirm_access_modal',
  /** Soft-confirm alert when dismissing the Heidi II confirm-access modal. */
  Heidi2ConfirmAccessDismissAlert = 'heidi_2_confirm_access_dismiss_alert',
  /** Work-home onboarding banner shown after Heidi II activation, before the tour. */
  Heidi2HomeChangeboardingBanner = 'heidi_2_home_changeboarding_banner',
  /** Top banner for Heidi II users who chose "Explore later" on the welcome. */
  Heidi2ExploreLaterBanner = 'heidi_2_explore_later_banner',
  /** Heidi II Home tour: the Home rail, then Recents on Home. */
  Heidi2EvidenceTourSpotlight = 'heidi_2_evidence_tour_spotlight',
  /** Work-home banner pointing at the Default page setting. */
  DefaultLandingPageBanner = 'default_landing_page_banner',
  /** "Get started" checklist on Heidi II Home. */
  Heidi2GetStartedChecklist = 'heidi_2_get_started_checklist',
  /** Full-screen Heidi II agents tour, from Welcome to Ready. */
  Heidi2AgentsOnboarding = 'heidi_2_agents_onboarding',
  /** The Heidi II intro takeover, reported the moment it opens. */
  IntroHeidi2 = 'intro_heidi_ii',
  Heidi2OnboardingWelcome = 'heidi_2_onboarding_welcome',
  Heidi2OnboardingHandoff = 'heidi_2_onboarding_handoff',
  Heidi2OnboardingTellHeidi = 'heidi_2_onboarding_tell_heidi',
  Heidi2OnboardingHandlesIt = 'heidi_2_onboarding_handles_it',
  Heidi2OnboardingReady = 'heidi_2_onboarding_ready',
  Heidi2InSessionRoutines = 'heidi_2_in_session_routines',
  Heidi2WorkHome = 'heidi_2_work_home',
  /** "Send patient summary" (PDF link) modal — SMS / email link to a secure PDF. */
  SendPatientSummaryModal = 'send_patient_summary_modal',
  // Template
  TemplateLibrary = 'template_library',
  MyTemplates = 'my_templates',
  ChooseTemplateTypeModal = 'choose_template_type_modal',
  ShareTemplateModal = 'share_template_modal',
  SharedTemplatePreview = 'shared_template_preview',
  TemplateCommunityDetails = 'template_community_details',
  TeamSettingsScreen = 'team_settings_screen',
  // Dictation
  DictationLogs = 'dictation_logs',
  DictationLogsSnippets = 'dictation_logs_snippets',
  DictationLogsVocab = 'dictation_logs_vocab',
  DictationLogsCommands = 'dictation_logs_commands',
  DictationLogsTone = 'dictation_logs_tone',
  DictationSettings = 'dictation_settings',
  DictationFeedbackPrompt = 'dictation_feedback_prompt',
  DictationFeedbackReasonPicker = 'dictation_feedback_reason_picker',
  DictationFeedbackConfirmation = 'dictation_feedback_confirmation',
  DictationLogsFeedbackReasonPicker = 'dictation_logs_feedback_reason_picker',
  DictateReengagement = 'dictate_reengagement',
  /** Desktop dictate onboarding full flow entry. */
  DictateOnboarding = 'dictate_onboarding',
  /** Returning-user sign-in variant (no lifecycle funnel). */
  DictateOnboardingReturningSignIn = 'dictate_onboarding_returning_sign_in',
  UNIVERSAL_DICTATION_ONBOARDING_MODAL = 'UNIVERSAL_DICTATION_ONBOARDING_MODAL',
  CURSOR_DICTATION_ONBOARDING_MODAL = 'CURSOR_DICTATION_ONBOARDING_MODAL',
  // Linked Sessions Confirmation Spotlight (auto-include history toggle)
  LinkedSessionsConfirmationSpotlight = 'linked_sessions_confirmation_spotlight',
  // Dictation Logs awareness spotlight (one-shot, points at "Dictate history" sidebar nav)
  DictationLogsSpotlight = 'dictation_logs_spotlight',
  // Dictate-page onboarding tour entry banner on /dictation-logs (SCR-7070)
  DictateTourBanner = 'dictate_tour_banner',
  // Session list spotlight tour — fired once when step 1 opens
  SessionListSpotlight = 'session_list_spotlight',
  // Routines onboarding spotlight tour — fired once when step 1 opens
  RoutinesSpotlight = 'routines_spotlight',
  // Transcribe Onboarding (from gamify trial extension)
  TranscribeOnboarding = 'transcribe_onboarding',
  // Sidenav
  Sidenav = 'sidenav',
  // Form Fill
  FormLibrary = 'form_library',
  // Haiku Integration
  HaikuSettingsPopup = 'haiku_settings_popup',
  // Trial & Discovery Modals
  ClinicianTrialModal = 'clinician_trial_modal',
  PracticeTrialModal = 'practice_trial_modal',
  PracticeInviteTeamModal = 'practice_invite_team_modal',
  SessionlessEvidenceAwarenessCard = 'sessionless_evidence_awareness_card',
  // Onboarding Popups
  SourceControlOnboardingTooltip = 'source_control_onboarding_tooltip',
  SourcesSpotlightTooltip = 'sources_spotlight_tooltip',
  SessionChatSidebarSpotlight = 'session_chat_sidebar_spotlight',
  PatientListIntroductionModal = 'patient_list_introduction_modal',
  PatientListNavSpotlight = 'patient_list_nav_spotlight',
  /** Health NZ enterprise-migration prompt for solo users. */
  HealthNzMigrationModal = 'health_nz_migration_modal',
  /** Post-consume in-session evidence quota System Toast. */
  EvidenceCreditConsumedToast = 'evidence_credit_consumed_toast',
  ScribeSpotlight = 'scribe_spotlight',
  ScribeCrossSellSpotlight = 'scribe_cross_sell_spotlight',
  ScribeOnboardingModal = 'scribe_onboarding_modal',
  ScribeOnboardingDictateModal = 'scribe_onboarding_dictate_modal',
  ShareChatOnboardingTooltip = 'share_chat_onboarding_tooltip',
  /** One-time spotlight: opt in to auto-including past Heidi sessions as note context */
  AutoPullHistorySpotlight = 'auto_pull_history_spotlight',
  // Share Session Announce Spotlight (image-based one-time spotlight launching direct sharing)
  ShareSessionAnnounceSpotlight = 'share_session_announce_spotlight',
  // Find & Replace
  FindReplaceModal = 'find_replace_modal',
  // Form Field Editor
  FormFieldEditor = 'form_field_editor',
  /** In-session template picker modal (template_button trigger). */
  TemplatePicker = 'template_picker', // Session Sharing
  ShareSessionModal = 'share_session_modal',
  SessionLinkShareOtp = 'session_link_share_otp',
  SessionLinkShareSession = 'session_link_share_session',
  // Consult Note History
  ConsultNoteHistoryModal = 'consult_note_history_modal',
  // Settings
  /** Account settings → Login details (MFA toggle lives here, MOD-1618). */
  SettingsAccountLoginDetails = 'settings_account_login_details',
  /** Account settings → Security (session-inactivity-timeout toggle lives here, MOD-1618). */
  SettingsAccountSecurity = 'settings_account_security',
  SettingsBilling = 'settings_billing',
  /** Settings → Defaults (session defaults, dictation, form field font size, etc.). */
  SettingsDefaults = 'settings_defaults',
  Letterhead = 'letterhead',
  PricingPlansModal = 'pricing_plans_modal',
  /** Seat checkout page: a Practice admin schedules members onto Max seats. */
  PracticeMaxSeatCheckout = 'practice_max_seat_checkout',
  /** Seat checkout page: a Scribe Team admin moves the team onto Practice. */
  ScribeTeamUpgradeCheckout = 'scribe_team_upgrade_checkout',
  /** Members page dialog that changes one member's Practice seat. */
  SeatChangeDialog = 'seat_change_dialog',
  /** Members page dialog opened from a member's Max seat request email. */
  MaxSeatRequestDialog = 'max_seat_request_dialog',
  CancellationConfirmationModal = 'cancellation_confirmation_modal',
  CancelReasonModal = 'cancel_reason_modal',
  RetentionDiscountOfferModal = 'retention_discount_offer_modal',
  ClinicianSessionOfferModal = 'clinician_session_offer_modal',
  // Desktop Auth
  PostSignIn = 'post_sign_in',
  /** Post-login workspace selector for users in 2+ workspaces. */
  WorkspaceSelector = 'workspace_selector',
  /** Sidebar workspace switcher menu. */
  WorkspaceTopMenu = 'workspace_top_menu',
  // Doc Export Suggestion Banner (Evidence — GRO-2885)
  DocExportSuggestionBanner = 'doc_export_suggestion_banner',
  /** Evidence chat input credits badge hover popover */
  CreditsRemainingPopover = 'credits_remaining_popover',
  /** Side-nav Clinician Max credit usage popover (GRO-4083 beta). */
  SideNavCreditUsagePopover = 'side_nav_credit_usage_popover',
  /** Strip banner in the last week of complimentary Heidi II Max usage. */
  Heidi2TrialEndBanner = 'heidi_2_trial_end_banner',
  /** T-7 upgrade modal for the end of complimentary Heidi II Max usage. */
  Heidi2TrialEndModal = 'heidi_2_trial_end_modal',
  /** Individual seat and billing period checkout before Stripe, from the T-7 modal. */
  Heidi2TrialEndCheckout = 'heidi_2_trial_end_checkout',
  /** Learn-more dialog explaining advanced evidence credits (Clinician / Practice). */
  AdvancedEvidenceQueryInfoModal = 'advanced_evidence_query_info_modal',
  // Integrations Marketplace
  IntegrationsMarketplace = 'integrations_marketplace',
  IntegrationsMarketplaceDetails = 'integrations_marketplace_details',
  IntegrationOnboardingModal = 'integration_onboarding_modal',
  IntegrationLegacyOnboardingModal = 'integration_legacy_onboarding_modal',
  IntegrationContactSalesModal = 'integration_contact_sales_modal',
  IntegrationContactTeamAdminModal = 'integration_contact_team_admin_modal',
  IntegrationDesktopOnlyModal = 'integration_desktop_only_modal',
  IntegrationUsageTypeSelectionModal = 'integration_usage_type_selection_modal',
  IntegrationDisconnectConfirmModal = 'integration_disconnect_confirm_modal',
  // Send-as-email — v2 (merged multi-recipient + link-share modal, default
  // when `release-scribe-send-as-email-merged-modal` + `allow_session_link_sharing`).
  SendAsEmailModal = 'send_as_email_modal',
  // Send-as-email — legacy single-recipient modal (off-state fallback for the
  // merged-modal flag / orgs without `allow_session_link_sharing`).
  SendAsEmailModalLegacy = 'send_as_email_modal_legacy',
  // Patient Summary turn-off prompt
  PatientSummaryTurnOffPrompt = 'patient_summary_turn_off_prompt',
  /** Hover education tooltip on the queued Patient Summary tab (GRO-3785). */
  PatientSummaryTabEducationTooltip = 'patient_summary_tab_education_tooltip',
  /** Confirmation toast after turning auto patient summaries off (GRO-3785). */
  PatientSummaryTurnedOffToast = 'patient_summary_turned_off_toast',
  /** Meddevice compliance-notification banner impression, MFA-setup variant (GRO-3989 / MOD-1618). */
  ComplianceNotificationMfaSetup = 'compliance_notification_mfa_setup',
  /** Meddevice compliance-notification banner impression, inactivity-logout variant (GRO-3989 / MOD-1618). */
  ComplianceNotificationInactivityLogout = 'compliance_notification_inactivity_logout',
}

export enum DeviceType {
  iOS = 'ios',
  Android = 'android',
  Unknown = 'unknown',
}

export enum ButtonAction {
  // Patient find-or-link funnel — fired when the user commits a typed
  // session title via the "Create unlinked session" footer row in the
  // combobox (no patient linked, no patient created). Closes the funnel
  // for users who reach the field but never engage with the patient
  // linkage flow.
  UnlinkedSessionCreated = 'unlinked_session_created',
  EndPromotedFreeTrialKeepPro = '[END PROMOTED FREE TRIAL] keep pro',
  EndPromotedFreeTrialDowngradeToFree = '[END PROMOTED FREE TRIAL] downgrade to free',
  SideBySideDropdownStartFreeTrial = '[SIDE BY SIDE][DROPDOWN] Start free trial',
  SideBySideDropdownOpenUpgradeProModal = '[SIDE BY SIDE][DROPDOWN] Open upgrade pro modal',
  ContextFileUpload = '[CONTEXT FILE] upload file',
  ContextFileDelete = '[CONTEXT FILE] delete file',
  LinkPatientDocuments = 'link_patient_documents',
  // Scribe layout V2 invite surfaces
  ScribeLayoutV2InviteNotNow = 'scribe_layout_v2_invite_not_now',
  ScribeLayoutV2InviteDelay = 'scribe_layout_v2_invite_delay',
  ScribeLayoutV2InviteClose = 'scribe_layout_v2_invite_close',
  ScribeLayoutV2GaInviteClose = 'scribe_layout_v2_ga_invite_close',
  ScribeLayoutV2InviteCardTryItNow = 'scribe_layout_v2_invite_card_try_it_now',
  ScribeLayoutV2InviteBannerTryItNow = 'scribe_layout_v2_invite_banner_try_it_now',
  ScribeLayoutV2TourBannerTakeTour = 'scribe_layout_v2_tour_banner_take_tour',
  ScribeLayoutV2TourBannerDismiss = 'scribe_layout_v2_tour_banner_dismiss',
  Heidi2AwarenessBannerEarlyAccess = 'heidi_2_awareness_banner_early_access',
  Heidi2AwarenessBannerDismiss = 'heidi_2_awareness_banner_dismiss',
  Heidi2RequestAccessBannerRequest = 'heidi_2_request_access_banner_request',
  Heidi2RequestAccessBannerLearnMore = 'heidi_2_request_access_banner_learn_more',
  Heidi2RequestAccessBannerDismiss = 'heidi_2_request_access_banner_dismiss',
  Heidi2OptInModalStart = 'heidi_2_opt_in_modal_start',
  Heidi2OptInModalOpenTerms = 'heidi_2_opt_in_modal_open_terms',
  Heidi2OptInModalCloseTerms = 'heidi_2_opt_in_modal_close_terms',
  Heidi2OptInModalDismiss = 'heidi_2_opt_in_modal_dismiss',
  Heidi2RequestAccessModalSend = 'heidi_2_request_access_modal_send',
  Heidi2RequestAccessModalCancel = 'heidi_2_request_access_modal_cancel',
  Heidi2RequestAccessModalDismiss = 'heidi_2_request_access_modal_dismiss',
  Heidi2ConfirmAccessModalConfirm = 'heidi_2_confirm_access_modal_confirm',
  Heidi2ConfirmAccessModalDismiss = 'heidi_2_confirm_access_modal_dismiss',
  Heidi2HomeChangeboardingBannerOpenTour = 'heidi_2_home_changeboarding_banner_open_tour',
  Heidi2HomeChangeboardingBannerDismiss = 'heidi_2_home_changeboarding_banner_dismiss',
  Heidi2ExploreLaterBannerOpenTour = 'heidi_2_explore_later_banner_open_tour',
  Heidi2ExploreLaterBannerDismiss = 'heidi_2_explore_later_banner_dismiss',
  DefaultLandingPageBannerManage = 'default_landing_page_banner_manage',
  DefaultLandingPageBannerDismiss = 'default_landing_page_banner_dismiss',
  Heidi2EvidenceTourShowMe = 'heidi_2_evidence_tour_show_me',
  Heidi2EvidenceTourGotIt = 'heidi_2_evidence_tour_got_it',
  Heidi2EvidenceTourDismiss = 'heidi_2_evidence_tour_dismiss',
  // The specific item is carried in `label`, not the action name.
  Heidi2GetStartedChecklistItem = 'heidi_2_get_started_checklist_item',
  Heidi2GetStartedChecklistBookDemo = 'heidi_2_get_started_checklist_book_demo',
  Heidi2GetStartedChecklistDismiss = 'heidi_2_get_started_checklist_dismiss',
  // How they left is carried in `label`: `explore_later` or `close`.
  Heidi2AgentsOnboardingDismiss = 'heidi_2_agents_onboarding_dismiss',
  Heidi2AgentsOnboardingBookDemo = 'heidi_2_agents_onboarding_book_demo',
  Heidi2AgentsOnboardingInteraction = 'heidi_2_agents_onboarding_interaction',
  Heidi2InSessionRoutines = 'heidi_2_in_session_routines',
  Heidi2WorkHomeContextSelected = 'heidi_2_work_home_context_selected',
  // Post-note quality "Improve your notes" — GRO-3284. The specific task is
  // carried in `feature` / `task_id` / `label`, not the action name.
  PostNoteQualityTaskCtaClicked = 'post_note_quality_task_cta_clicked',
  // GRO-3284 — user picked a note in the side-by-side model comparison. The
  // chosen model ('default' | 'sonnet_4') is carried in `label`.
  SideBySideNoteSelected = 'side_by_side_note_selected',
  // Trial Extension
  TrialExtensionTaskClicked = 'trial_extension_task_clicked',
  TrialExtensionClose = 'trial_extension_close',
  TrialExtensionUpgradeToPractice = 'trial_extension_upgrade_to_practice',
  TrialExtensionUpgradeToClinicianPro = 'trial_extension_upgrade_to_clinician_pro',
  TrialExtensionUpgradeToEvidence = 'trial_extension_upgrade_to_evidence',
  TrialExtensionAddEmail = 'trial_extension_add_email',
  TrialExtensionSendInvites = 'trial_extension_send_invites',
  TrialExtensionBack = 'trial_extension_back',
  TrialExtensionSidenavButtonClicked = 'trial_extension_sidenav_button_clicked',
  TrialExtensionCompletionTooltipDismissed = 'trial_extension_completion_tooltip_dismissed',
  /** Student/trainee verification CTA (clinician upgrade nudge or trial extension modal). */
  StudentVerificationCtaClicked = 'student_verification_cta_clicked',
  /** Sidebar live 1:1 demo CTA — the `liveDemoLink` variant of the demo slot; market/language/account-age targeting lives in the `country-config` PostHog payload. */
  SidebarLiveDemoCtaClicked = 'sidebar_live_demo_cta_clicked',
  NinetyDayTrialEndingReminderSeeSummary = 'ninety_day_trial_ending_reminder_see_summary',
  NinetyDayTrialEndingReminderClose = 'ninety_day_trial_ending_reminder_close',
  NinetyDayTrialRecapUpgrade = 'ninety_day_trial_recap_upgrade',
  /** Team admin on a non–Stripe-managed team: contact sales from trial recap. */
  NinetyDayTrialRecapContactSales = 'ninety_day_trial_recap_contact_sales',
  PracticeUpgradeModalContactSales = 'practice_upgrade_modal_contact_sales',
  ClinicianUpgradeModalContactSales = 'clinician_upgrade_modal_contact_sales',
  /** Primary upgrade CTA in the Clinician upgrade modal. */
  ClinicianUpgradeModalUpgrade = 'clinician_upgrade_modal_upgrade',
  /** Primary upgrade CTA in the Practice upgrade modal. */
  PracticeUpgradeModalUpgrade = 'practice_upgrade_modal_upgrade',
  /** Secondary "Talk to us" CTA in the Practice upgrade modal (90-day trial variant). */
  PracticeUpgradeModalTalkToUs = 'practice_upgrade_modal_talk_to_us',
  NinetyDayTrialRecapTryNow = 'ninety_day_trial_recap_try_now',
  NinetyDayTrialRecapRemindMeLater = 'ninety_day_trial_recap_remind_me_later',
  /** Non-admin team member asks their admin to upgrade (trial recap banner). */
  NinetyDayTrialRecapRequestAdminUpgrade = 'ninety_day_trial_recap_request_admin_upgrade',
  /** Submit callback request on the trial upgrade request callback modal. */
  TrialUpgradeRequestCallbackSend = 'trial_upgrade_request_callback_send',
  /** Cancel on the trial upgrade request callback modal. */
  TrialUpgradeRequestCallbackCancel = 'trial_upgrade_request_callback_cancel',
  /** Back control on the trial upgrade request callback modal. */
  TrialUpgradeRequestCallbackBack = 'trial_upgrade_request_callback_back',
  /** Primary CTA on the post–Clinician trial ended modal (queue). */
  ClinicianTrialEndedKeepClinician = 'clinician_trial_ended_keep_clinician',
  /** Primary CTA on the post–Practice trial ended modal (queue). */
  ClinicianTrialEndedKeepPractice = 'clinician_trial_ended_keep_practice',
  /** Secondary contact-sales CTA on the post–Practice trial ended modal. */
  ClinicianTrialEndedTalkToUs = 'clinician_trial_ended_talk_to_us',
  /** Team admin on a non–Stripe-managed team: single contact-sales CTA on trial-ended modal (Practice). */
  ClinicianTrialEndedContactSales = 'clinician_trial_ended_contact_sales',
  /** Non-admin team member: single "Request to upgrade" CTA on trial-ended modal — opens team upgrade request flow. */
  ClinicianTrialEndedRequestToUpgrade = 'clinician_trial_ended_request_to_upgrade',
  /** Secondary "Remain on Scribe Plus / Team" CTA on the trial-ended modal — closes the modal without upgrading. */
  ClinicianTrialEndedRemain = 'clinician_trial_ended_remain',
  /** "Learn more" CTA on the post–trial-end discreet corner card (opens the big trial-ended modal). */
  NinetyDayTrialEndDiscreetLearnMore = 'ninety_day_trial_end_discreet_learn_more',
  /** "Learn more" link on the Evidence credits remaining hover popover. */
  CreditsRemainingPopoverLearnMore = 'credits_remaining_popover_learn_more',
  /** Upgrade CTA on the side-nav credit usage popover. */
  SideNavCreditUsagePopoverUpgrade = 'side_nav_credit_usage_popover_upgrade',
  /** Close (×) on the side-nav credit usage popover. */
  SideNavCreditUsagePopoverClose = 'side_nav_credit_usage_popover_close',
  /** "Keep usage" on the complimentary usage end banner (active members). */
  Heidi2TrialEndBannerKeepUsage = 'heidi_2_trial_end_banner_keep_usage',
  /** "Give Heidi a task" on the complimentary usage end banner (inactive members). */
  Heidi2TrialEndBannerGiveTask = 'heidi_2_trial_end_banner_give_task',
  /** Close (×) on the complimentary usage end banner. */
  Heidi2TrialEndBannerDismiss = 'heidi_2_trial_end_banner_dismiss',
  /** "Get Clinician Max" on the T-7 complimentary usage end modal. */
  Heidi2TrialEndModalGetClinicianMax = 'heidi_2_trial_end_modal_get_clinician_max',
  /** "View all plans" on the T-7 complimentary usage end modal. */
  Heidi2TrialEndModalViewAllPlans = 'heidi_2_trial_end_modal_view_all_plans',
  /** "Continue" on the individual seat checkout, on to Stripe. */
  Heidi2TrialEndCheckoutContinue = 'heidi_2_trial_end_checkout_continue',
  /** "Cancel" on the individual seat checkout. */
  Heidi2TrialEndCheckoutCancel = 'heidi_2_trial_end_checkout_cancel',
  /** Primary upgrade CTA on the Clinician Max upgrade modal. */
  ClinicianMaxUpgradeModalUpgrade = 'clinician_max_upgrade_modal_upgrade',
  /** Secondary stay-on-current-plan CTA on the Clinician Max upgrade modal. */
  ClinicianMaxUpgradeModalStay = 'clinician_max_upgrade_modal_stay',
  /** Close on the advanced evidence credits info modal. */
  AdvancedEvidenceQueryInfoModalClose = 'advanced_evidence_query_info_modal_close',
  /** Upgrade CTA on the advanced evidence credits info modal. */
  AdvancedEvidenceQueryInfoModalUpgrade = 'advanced_evidence_query_info_modal_upgrade',
  /** Close (×) on the post–trial-end discreet corner card. */
  NinetyDayTrialEndDiscreetClose = 'ninety_day_trial_end_discreet_close',
  /** GRO-3976: Trial upgrade nudge icon clicked (opens upgrade path). */
  TrialUpgradeNudgeOpen = 'trial_upgrade_nudge_open',
  /** GRO-3976: "Keep Heidi" clicked in the trial upgrade nudge popover. */
  TrialUpgradeNudgeKeepHeidi = 'trial_upgrade_nudge_keep_heidi',
  EvidencePostUpgradeWelcomeSeeWhatsNew = 'evidence_post_upgrade_welcome_see_whats_new',
  EvidencePostUpgradeWelcomeClose = 'evidence_post_upgrade_welcome_close',
  EvidencePostUpgradeOnboardingNext = 'evidence_post_upgrade_onboarding_next',
  EvidencePostUpgradeOnboardingViewGuide = 'evidence_post_upgrade_onboarding_view_guide',
  EvidencePostUpgradeOnboardingLearnMore = 'evidence_post_upgrade_onboarding_learn_more',
  EvidencePostUpgradeOnboardingGotIt = 'evidence_post_upgrade_onboarding_got_it',
  EvidencePostUpgradeOnboardingTryNow = 'evidence_post_upgrade_onboarding_try_now',
  SidebarReferralButtonClicked = 'sidebar_referral_button_clicked',
  SidebarFooterMoreOpened = 'sidebar_footer_more_opened',
  /** Generic nav-v3 sidebar interaction — the specific control is carried in `label`, not the action name (recents load more/filter, chat row pin/rename/copy/delete, agent home panel toggle, recent-visit open). */
  NavV3ButtonClicked = 'nav_v3_button_clicked',
  TrialExploreLater = 'trial_explore_later',
  TemplateCreateNew = '[TEMPLATE] create new',
  TemplateUseExisting = '[TEMPLATE] use existing',
  TemplateStartFromScratch = '[TEMPLATE] start from scratch',
  CreateNoteOrDocument = '[Template] create note or document',
  CreateNoteWhilePaused = '[NOTE] create while paused',
  // Choose Template Type Modal
  ChooseTemplateTypeContinue = 'choose_template_type_continue',
  ChooseTemplateTypeCancel = 'choose_template_type_cancel',
  ChooseTemplateTypeClose = 'choose_template_type_close',
  ChooseTemplateTypeOptionSelected = 'choose_template_type_option_selected',
  // Template Sharing
  ShareTemplateOpen = '[SHARE TEMPLATE] open modal',
  ShareTemplateCopyLink = '[SHARE TEMPLATE] copy link',
  ShareTemplateSendEmail = '[SHARE TEMPLATE] send email',
  ShareTemplateClose = '[SHARE TEMPLATE] close modal',
  ShareTemplateAccessTab = '[SHARE TEMPLATE] access tab',
  ShareTemplateShareCopyTab = '[SHARE TEMPLATE] share copy tab',
  SharedTemplateSignUp = '[SHARED TEMPLATE] sign up',
  SharedTemplateUse = '[SHARED TEMPLATE] use template',
  FormPreviewFullscreen = '[FORM PREVIEW] open fullscreen',
  // Practice Trial
  PracticeTrialGetStarted = '[PRACTICE TRIAL] get started',
  PracticeTrialClose = '[PRACTICE TRIAL] close',
  PracticeInviteAddAnother = '[PRACTICE INVITE] add another',
  PracticeInviteSendInvites = '[PRACTICE INVITE] send invites',
  PracticeInviteContinue = '[PRACTICE INVITE] continue',
  PracticeInviteSkip = '[PRACTICE INVITE] skip',
  PracticeInviteClose = '[PRACTICE INVITE] close',
  EndPracticeTrialUpgrade = '[END PRACTICE TRIAL] upgrade',
  EndPracticeTrialDowngrade = '[END PRACTICE TRIAL] downgrade',
  EndPracticeTrialClose = '[END PRACTICE TRIAL] close',
  EndPracticeTrialExtend = '[END PRACTICE TRIAL] extend',
  ConfirmPracticeDowngradeClose = '[CONFIRM PRACTICE DOWNGRADE] close',
  ConfirmPracticeDowngradeGoBack = '[CONFIRM PRACTICE DOWNGRADE] go back',
  ConfirmPracticeDowngradeConfirm = '[CONFIRM PRACTICE DOWNGRADE] confirm',
  TeamAddMember = '[TEAM] add member',
  // Web Dictation Onboarding — TryIt step
  WebDictationOnboardingDownloadDesktopClicked = '[WEB DICTATION ONBOARDING] download desktop clicked',
  // Heidi Remote web download guide (APP-7445)
  WebRemoteDownloadDesktopClicked = '[HEIDI REMOTE WEB] download desktop clicked',
  // Desktop Dictation Onboarding — TryIt step
  DictationOnboardingChangeShortcutClicked = '[DICTATION ONBOARDING] change shortcut clicked',
  DictationOnboardingTryItLearnMoreClicked = '[DICTATION ONBOARDING] try-it learn more clicked',
  DictationOnboardingTryItDisableShortcutClicked = '[DICTATION ONBOARDING] try-it disable shortcut clicked',
  DictationOnboardingOkGotItClicked = '[DICTATION ONBOARDING] ok got it clicked',
  // Desktop Dictation Onboarding — Change Shortcut step
  DictationOnboardingKeepDefaultClicked = '[DICTATION ONBOARDING] keep default clicked',
  DictationOnboardingChangeShortcutLearnMoreClicked = '[DICTATION ONBOARDING] change-shortcut learn more clicked',
  DictationOnboardingChangeShortcutDisableShortcutClicked = '[DICTATION ONBOARDING] change-shortcut disable shortcut clicked',
  DictationOnboardingSaveSetupClicked = '[DICTATION ONBOARDING] save setup clicked',
  // Desktop Dictate Onboarding flow (SCR-5041)
  DictateOnboardingBack = 'dictate_onboarding_back',
  DictateOnboardingContinue = 'dictate_onboarding_continue',
  DictateOnboardingPermissionAllow = 'dictate_onboarding_permission_allow',
  DictateOnboardingAccessibilityPrePromptContinue = 'dictate_onboarding_accessibility_pre_prompt_continue',
  DictateOnboardingAccessibilityPrePromptNotNow = 'dictate_onboarding_accessibility_pre_prompt_not_now',
  DictateOnboardingTroubleshootingClicked = 'dictate_onboarding_troubleshooting_clicked',
  DictateOnboardingHotkeyConfirmed = 'dictate_onboarding_hotkey_confirmed',
  DictateOnboardingSignInContinue = 'dictate_onboarding_sign_in_continue',
  DictateOnboardingChangeShortcutClicked = 'dictate_onboarding_change_shortcut_clicked',
  DictateOnboardingKeepDefaultClicked = 'dictate_onboarding_keep_default_clicked',
  DictateOnboardingSaveSetupClicked = 'dictate_onboarding_save_setup_clicked',
  DictateOnboardingOkGotItClicked = 'dictate_onboarding_ok_got_it_clicked',
  // General dictate-intent re-engagement card (SCR-7071)
  DictateReengagementGotIt = 'dictate_reengagement_got_it',
  DictateReengagementShowMeHow = 'dictate_reengagement_show_me_how',
  // Dictation History page (/dictation-logs)
  DictationLogsRefresh = 'dictation_logs_refresh',
  DictationLogsSearch = 'dictation_logs_search',
  DictationLogsFilter = 'dictation_logs_filter',
  DictationLogCopy = 'dictation_log_copy',
  DictationLogDelete = 'dictation_log_delete',
  DictationLogsBulkDelete = 'dictation_logs_bulk_delete',
  DictationLogsTabSwitch = 'dictation_logs_tab_switch',
  DictationToneChanged = 'dictation_tone_changed',
  DictationVerbalCommandsToggled = 'dictation_verbal_commands_toggled',
  // Dictation Settings (/settings/defaults#dictation)
  DictationHotkeySave = 'dictation_hotkey_save',
  // Desktop dictation floating widget (cursor dictation result bubble)
  DictationWidgetCopy = 'dictation_widget_copy',
  DictationFeedbackNotNow = 'dictation_feedback_not_now',
  DictationFeedbackSeeHistory = 'dictation_feedback_see_history',
  DictationLogsFeedbackCancel = 'dictation_logs_feedback_cancel',
  // Transcribe Onboarding (from gamify trial extension)
  TranscribeOnboardingDismissedClose = '[TRANSCRIBE ONBOARDING] dismissed via close button',
  TranscribeOnboardingLearnMoreClick = '[TRANSCRIBE ONBOARDING] learn more link clicked',
  TranscribeOnboardingTryNowClick = '[TRANSCRIBE ONBOARDING] try now button clicked',
  // Share Chat Onboarding Tooltip (first-time introduction to chat sharing)
  ShareChatOnboardingDismissedClose = '[SHARE CHAT ONBOARDING] dismissed via close button',
  ShareChatOnboardingShareClick = '[SHARE CHAT ONBOARDING] share button clicked',
  AutoPullHistoryDismissedClose = '[AUTO PULL HISTORY] dismissed via close button',
  AutoPullHistoryIncludeClick = '[AUTO PULL HISTORY] include CTA clicked',
  AutoPullHistoryDontIncludeClick = "[AUTO PULL HISTORY] don't include CTA clicked",
  // Share Session Announce Spotlight
  ShareSessionAnnounceDismissedClose = '[SHARE SESSION ANNOUNCE] dismissed via close button',
  ShareSessionAnnounceShareClick = '[SHARE SESSION ANNOUNCE] share CTA clicked',
  // Web Dictation
  WebDictationHelpLinkClicked = '[UNIVERSAL DICTATION] help link clicked',
  // Sidenav Create Template Dropdown
  SidenavCreateTemplateDropdownOpened = '[SIDENAV] create template dropdown opened',
  SidenavCreateNoteTemplateClicked = '[SIDENAV] create note template clicked',
  SidenavCreateDocumentTemplateClicked = '[SIDENAV] create document template clicked',
  SidenavCreateAutoPdfTemplateClicked = '[SIDENAV] create auto-pdf template clicked',
  // Sessions
  SessionsTidyUp = '[SESSIONS] tidy up',
  SessionListRefreshed = '[SESSIONS] list refreshed',
  SessionListTabChanged = '[SESSIONS] tab changed',
  SessionListStartNow = 'session_list_start_now',
  SessionListPauseNow = 'session_list_pause_now',
  SessionListResumeNow = 'session_list_resume_now',
  SessionListStopNow = 'session_list_stop_now',
  DeleteAccountModalOpenClicked = '[SETTINGS] DeleteAccountModalOpenClicked ',
  DeleteAccountConfirm = '[SETTINGS] delete account confirm',
  LogoutEverywhereModalOpenClicked = '[SETTINGS] logout everywhere modal open clicked',
  LogoutEverywhereConfirm = '[SETTINGS] logout everywhere confirm',
  // Post-login workspace selector
  WorkspaceSelectorWorkspaceSelected = 'workspace_selector_workspace_selected',
  WorkspaceSelectorJoinTeam = 'workspace_selector_join_team',
  // Sidebar workspace switcher menu
  WorkspaceTopMenuWorkspaceSelected = 'workspace_top_menu_workspace_selected',
  WorkspaceTopMenuSwitchBlockedRecording = 'workspace_top_menu_switch_blocked_recording',
  WorkspaceTopMenuJoinTeam = 'workspace_top_menu_join_team',
  // Evidence
  EvidenceSidebarClicked = '[EVIDENCE] sidebar clicked',
  EvidenceSourcesListClicked = '[EVIDENCE] sources list clicked',
  EvidenceSourcesPresetClicked = '[EVIDENCE] sources preset clicked',
  EvidenceSourcesCustomClicked = '[EVIDENCE] sources custom clicked',
  EvidenceLocalGuidelinesClicked = '[EVIDENCE] local guidelines clicked',
  EvidenceWebSearchClicked = '[EVIDENCE] web search clicked',
  EvidenceProTrialClicked = '[EVIDENCE] pro trial clicked',
  EvidenceSignInClicked = '[EVIDENCE] sign in clicked',
  EvidenceSignUpClicked = '[EVIDENCE] sign up clicked',
  EvidenceNewChatClicked = '[EVIDENCE] new chat clicked',
  EvidenceDeleteChatClicked = '[EVIDENCE] delete chat clicked',
  EvidenceSaveChatClicked = 'save_chat_clicked',
  EvidenceUnsaveChatClicked = 'unsave_chat_clicked',
  EvidencePastSessionClicked = '[EVIDENCE] past session clicked',
  EvidenceSidebarOpenClicked = '[EVIDENCE] sidebar open clicked',
  EvidenceSidebarCloseClicked = '[EVIDENCE] sidebar close clicked',
  EvidenceToastViewClicked = '[EVIDENCE] toast view clicked',
  SessionlessEvidenceAwarenessCtaClicked = 'evidence_awareness_popup_cta_clicked',
  SessionlessEvidenceAwarenessDismissClicked = 'evidence_awareness_popup_dismiss_clicked',
  // Evidence - object_action pattern (new naming convention)
  EvidenceSuggestionSelected = 'evidence_suggestion_selected',
  /** Document suggestion banner overflow: "Hide these suggestions" */
  HideDocumentSuggestions = 'hide_document_suggestions',
  EvidenceSourceSelected = 'evidence_source_selected',
  AttachSession = 'attach_session',
  AttachPatient = 'attach_patient',
  UploadFile = 'upload_file',
  AddToNote = 'add_to_note',
  ReplaceNote = 'replace_note',
  // Ask Heidi (Floating Input & Peek Sheet)
  AskHeidiSuggestionChipClicked = '[ASK HEIDI] suggestion chip clicked',
  AskHeidiShortcutChipClicked = '[ASK HEIDI] shortcut chip clicked',
  AskHeidiSelectionActionClicked = '[ASK HEIDI] selection action clicked',
  AskHeidiSelectionAskAnythingClicked = '[ASK HEIDI] selection ask anything clicked',
  AskHeidiSelectionMenuShown = '[ASK HEIDI] selection menu shown',
  AskHeidiInlineEditAccepted = '[ASK HEIDI] inline edit accepted',
  AskHeidiInlineEditDismissed = '[ASK HEIDI] inline edit dismissed',
  AskHeidiInlineInsightReadMore = '[ASK HEIDI] inline insight read more clicked',
  AskHeidiInlineInsightViewInTranscript = '[ASK HEIDI] inline insight view in transcript clicked',
  AskHeidiInlineEditAskInChat = '[ASK HEIDI] inline edit ask in chat clicked',
  AskHeidiInlineResponseFeedback = '[ASK HEIDI] inline response feedback',
  AskHeidiSuggestionsExpanded = '[ASK HEIDI] suggestions expanded',
  AskHeidiSettingsClicked = '[ASK HEIDI] settings clicked',
  AskHeidiShortcutsOpened = '[ASK HEIDI] shortcuts opened',
  AskHeidiShortcutsBrowseAll = '[ASK HEIDI] shortcuts browse all',
  AskHeidiDictateClicked = '[ASK HEIDI] dictate clicked',
  /**
   * centre-transcription preview: fired once when the floating bar recentres
   * into the content area during transcription (note/document-like tabs).
   * Props carry `session_tab` (session canvas TabType).
   */
  AskHeidiCentredShown = '[ASK HEIDI] centred bar shown',
  // Note tab footer cluster (Personalisation / Codes / Tasks / Verify)
  NoteFooterToggleClicked = '[NOTE FOOTER] toggle clicked',
  // Form Field Editor
  FormEditorGetSupport = 'form_editor_get_support',
  FormEditorTitleHintDismissed = 'form_editor_title_hint_dismissed',
  FormEditorFieldSelected = 'form_editor_field_selected',
  FormEditorZoomSelected = 'form_editor_zoom_selected',
  FormEditorUndo = 'form_editor_undo',
  FormEditorRedo = 'form_editor_redo',
  FormEditorFieldCopied = 'form_editor_field_copied',
  FormEditorFieldPasted = 'form_editor_field_pasted',
  FormEditorRegenerateFields = 'form_editor_regenerate_fields',
  // Auto Mode
  AutoModeSelected = 'auto_mode_selected',
  // Start Recording Split Dropdown (transcribe-mode picker on the session header).
  // These fire when the user picks an option in the dropdown — they do NOT
  // start transcription. `transcription_started` only fires once recording
  // actually begins (see analyticsScribeModel.sessionStartRecording).
  StartRecordingDropdownTranscribeClicked = 'start_recording_dropdown_transcribe_clicked',
  StartRecordingDropdownDictateClicked = 'start_recording_dropdown_dictate_clicked',
  StartRecordingDropdownTelehealthClicked = 'start_recording_dropdown_telehealth_clicked',
  StartRecordingDropdownUploadClicked = 'start_recording_dropdown_upload_clicked',
  // Session Link Share (unauthenticated)
  SessionLinkShareSignIn = 'session_link_share_sign_in',
  SessionLinkShareSignUp = 'session_link_share_sign_up',
  SessionLinkShareTabChanged = 'session_link_share_tab_changed',
  // Session list spotlight tour
  SessionListSpotlightNext = '[SESSION LIST SPOTLIGHT] next step clicked',
  SessionListSpotlightCompleted = '[SESSION LIST SPOTLIGHT] completed',
  SessionListSpotlightDismissed = '[SESSION LIST SPOTLIGHT] dismissed early',
  RoutinesSpotlightNext = '[ROUTINES SPOTLIGHT] next step clicked',
  RoutinesSpotlightCompleted = '[ROUTINES SPOTLIGHT] completed',
  RoutinesSpotlightDismissed = '[ROUTINES SPOTLIGHT] dismissed early',
  // Linked History Confirmation Spotlight (auto-include history toggle)
  LinkedSessionsConfirmationTurnOn = '[LINKED SESSIONS CONFIRMATION SPOTLIGHT] turn on clicked',
  LinkedSessionsConfirmationTurnOff = '[LINKED SESSIONS CONFIRMATION SPOTLIGHT] turn off clicked',
  LinkedSessionsConfirmationKeepOn = '[LINKED SESSIONS CONFIRMATION SPOTLIGHT] keep on clicked',
  LinkedSessionsConfirmationKeepOff = '[LINKED SESSIONS CONFIRMATION SPOTLIGHT] keep off clicked',
  LinkedSessionsConfirmationDismissedClose = '[LINKED SESSIONS CONFIRMATION SPOTLIGHT] dismissed via close button',
  // Settings — Billing
  SettingsBillingCancelTrial = 'settings_billing_cancel_trial',
  SettingsBillingTrialUpgradeNowClicked = 'settings_billing_trial_upgrade_now_clicked',
  SettingsBillingManageBilling = 'settings_billing_manage_billing',
  SettingsBillingCurrentPlanUpgrade = 'settings_billing_current_plan_upgrade',
  SettingsBillingEnterpriseGetInTouch = 'settings_billing_enterprise_get_in_touch',
  SettingsBillingKeepPlan = 'settings_billing_keep_plan',
  SettingsBillingCancelPlan = 'settings_billing_cancel_plan',
  SettingsBillingContinueToCancel = 'settings_billing_continue_to_cancel',
  SettingsBillingSkipCancelReason = 'settings_billing_skip_cancel_reason',
  // Manage plan overflow menu on settings billing — opens pricing plans modal
  SettingsBillingManagePlanMenuChangePlan = 'settings_billing_manage_plan_menu_change_plan',
  SettingsBillingCancelPlanConfirm = 'settings_billing_cancel_plan_confirm',
  SettingsBillingAcceptDiscountOffer = 'settings_billing_accept_discount_offer',
  SettingsBillingAcceptClinicianSessionOffer = 'settings_billing_accept_clinician_session_offer',
  /** Pricing plans modal */
  PricingPlansModalDowngradeToFree = 'pricing_plans_modal_downgrade_to_free',
  PricingPlansModalGetPlan = 'pricing_plans_modal_get_plan',
  PricingPlansModalKeepCurrentPlan = 'pricing_plans_modal_keep_current_plan',
  PricingPlansModalGetInTouch = 'pricing_plans_modal_get_in_touch',
  /** Trial banner in the pricing plans modal — opens the gamified trial extension. */
  PricingPlansModalExtendTrial = 'pricing_plans_modal_extend_trial',
  /** User dismissal of the pricing plans modal (×, overlay, Escape, drag). */
  PricingPlansModalClose = 'pricing_plans_modal_close',
  /** Team seat checkout page (Practice Max scheduling or Scribe Team move). */
  TeamSeatCheckoutBack = 'team_seat_checkout_back',
  TeamSeatCheckoutUpgrade = 'team_seat_checkout_upgrade',
  /** Upgrade with every scheduled member unticked: cancels their Max upgrades. */
  TeamSeatCheckoutCancelSchedule = 'team_seat_checkout_cancel_schedule',
  TeamSeatCheckoutSeatSelected = 'team_seat_checkout_seat_selected',
  /** Members page seat change dialog. */
  SeatChangeDialogNext = 'seat_change_dialog_next',
  SeatChangeDialogBack = 'seat_change_dialog_back',
  SeatChangeDialogConfirm = 'seat_change_dialog_confirm',
  SeatChangeDialogClose = 'seat_change_dialog_close',
  /** Members page Max seat request dialog. */
  MaxSeatRequestDialogConfirm = 'max_seat_request_dialog_confirm',
  MaxSeatRequestDialogClose = 'max_seat_request_dialog_close',

  // Community Template detail page
  CommunityTemplateImport = 'community_template_import',
  CommunityTemplateFormDemoSubmit = 'community_template_form_demo_submit',
  CommunityTemplateFormDemoTrySampleClick = 'community_template_form_demo_try_sample_click',
  CommunityTemplateFormDemoDownload = 'community_template_form_demo_download',
  CommunityTemplateFormDemoSignupClick = 'community_template_form_demo_signup_click',

  // Doc Export Suggestion Banner (Evidence — GRO-2885)
  DocExportBannerCreateWordDoc = 'doc_export_banner_create_word_doc',
  DocExportBannerCreatePdf = 'doc_export_banner_create_pdf',
  DocExportBannerDismiss = 'doc_export_banner_dismiss',
  // Integrations Marketplace - Listings
  IntegrationCardClicked = 'integration_card_clicked',
  IntegrationSearched = 'integration_searched',
  IntegrationFilterChanged = 'integration_filter_changed',
  ConnectorsLayoutChanged = 'connectors_layout_changed',
  IntegrationRequestClicked = 'integration_request_clicked',
  // Integrations Marketplace - Details
  IntegrationOnboardingStarted = 'integration_onboarding_started',
  IntegrationLearnMoreClicked = 'integration_learn_more_clicked',
  IntegrationSectionNavClicked = 'integration_section_nav_clicked',
  // Integrations Marketplace - Disconnect
  IntegrationDisconnectClicked = 'integration_disconnect_clicked',
  IntegrationDisconnectConfirmed = 'integration_disconnect_confirmed',
  IntegrationDisconnectCancelled = 'integration_disconnect_cancelled',
  // Integrations Marketplace - Modals
  IntegrationOnboardingStepViewed = 'integration_onboarding_step_viewed',
  IntegrationOnboardingStepCompleted = 'integration_onboarding_step_completed',
  IntegrationOnboardingContinue = 'integration_onboarding_continue',
  IntegrationOnboardingBack = 'integration_onboarding_back',
  IntegrationOnboardingClose = 'integration_onboarding_close',
  IntegrationOnboardingComplete = 'integration_onboarding_complete',
  IntegrationContactSalesSubmit = 'integration_contact_sales_submit',
  IntegrationContactSalesClose = 'integration_contact_sales_close',
  IntegrationContactTeamAdminClose = 'integration_contact_team_admin_close',
  IntegrationDesktopOnlyDownload = 'integration_desktop_only_download',
  IntegrationDesktopOnlyClose = 'integration_desktop_only_close',
  IntegrationUsageTypeSelected = 'integration_usage_type_selected',
  // "Send patient summary" (PDF link) flow — a single action covering the
  // toolbar/More-menu open, the SMS<->Email method switch, the message preview,
  // and the (mocked) send. The specific step is carried in `label`
  // (`open` / `switch_method_*` / `preview_*` / `send_*`) and the launch
  // surface in `click_source` (`toolbar_button` / `more_menu`).
  SendToPatient = 'send_to_patient',
  // Early meeting flow / SLG entry choice CTAs
  SlgChoiceSignup = 'slg_choice_signup',
  SlgChoiceBookDemo = 'slg_choice_book_demo',
  // Global sidenav "+" create menu — a single action for the trigger tap and
  // every option. The chosen option is carried in `label` (open / session /
  // chat / task / routine / template / pdf_form) so dashboards can see whether
  // a "+" tap leads to a session or another artifact.
  SidenavCreateMenuClicked = 'sidenav_create_menu_clicked',
  // V2 session page — a single action for every button across the page chrome
  // (right-panel cards + the header bar). The region is carried in `location`
  // (`session_card` / `tab_card` / `tasks_card` / `session_header`) and the
  // specific control in `label`, so dashboards break the actions down per
  // region without a per-button enum entry.
  SessionButtonClicked = 'session_button_clicked',
  // Send-as-email modal — single action, control discriminated by `label`
  // ('close' | 'cancel' | 'send_email').
  SendAsEmailModalButtonClicked = 'send_as_email_modal_button_clicked',
  // Session note "more" (3-dot ellipsis) dropdown — single action, control
  // discriminated by `label` ('open' for trigger; menu item slug otherwise,
  // e.g. 'send_as_email').
  MoreDropdownButtonClicked = 'more_dropdown_button_clicked',
  // Push to EHR (in-session "Push to {EHR}" button — Hero Health / EMIS etc.).
  // Top-of-funnel click fired BEFORE the attachment picker and
  // `integration_started` (which only fires once the modal actually opens), so
  // it's the true "did anyone try" denominator — lets dashboards tell genuine
  // no-usage apart from an entry/attachment break when the funnel flatlines.
  // EHR is carried in `ehr_provider`.
  PushNoteToEhrClicked = 'push_note_to_ehr_clicked',
  // Clinician switched onto the patient-summary tab — the discoverability
  // numerator against sessions where the tab was present. `location` ∈ v1 | v2.
  PatientSummaryTabOpened = 'patient_summary_tab_opened',
  // Patient Summary tab UX — single action per surface, control discriminated by
  // `label`, and V1 vs V2 layout by `location`.
  // Tab actions: label ∈ open | regenerate | remove_from_queue |
  // manage_in_settings | delete; location ∈ v1 | v2. `click_source` splits the
  // context menu from the tab's × close button, which share the delete label.
  PatientSummaryTabMenuClicked = 'patient_summary_tab_menu_clicked',
  // Turn-off prompt dialog: label ∈ turn_off | keep_on | dismiss.
  PatientSummaryTurnOffPromptClicked = 'patient_summary_turn_off_prompt_clicked',
  // Editor bubble/text menu — shared building blocks (auto-instrumented).
  // Segment by `editor_type`; the acting editor is the `editor_type` prop.
  TextMenuBold = 'text_menu_bold',
  TextMenuItalic = 'text_menu_italic',
  TextMenuUnderline = 'text_menu_underline',
  TextMenuBulletList = 'text_menu_bullet_list',
  TextMenuOrderedList = 'text_menu_ordered_list',
  TextMenuCodeBlock = 'text_menu_code_block',
  TextMenuFindReplace = 'text_menu_find_replace',
  TextMenuCreateSnippet = 'text_menu_create_snippet',
  // "Edit template" button in the patient-summary action bar.
  PatientSummaryEditTemplateClicked = 'patient_summary_edit_template_clicked',
  // `Acknowledged` is the "OK" button — the only one that dismisses permanently.
  HealthNzMigrationUpdateEmail = 'health_nz_migration_update_email',
  HealthNzMigrationAcknowledged = 'health_nz_migration_acknowledged',
  HealthNzMigrationContactSupport = 'health_nz_migration_contact_support',
  LetterheadButtonClicked = 'letterhead_button_clicked',
  /** Meddevice compliance-notification banner row clicked through to its settings section (GRO-3989 / MOD-1618). */
  ComplianceNotificationClicked = 'compliance_notification_clicked',
  /** Meddevice compliance-notification banner deleted from the notifications list (GRO-3989 / MOD-1618). */
  ComplianceNotificationDismissed = 'compliance_notification_dismissed',
  /** Two-factor authentication turned on, possibly attributed back to a compliance notification (MOD-1618). */
  MfaEnabled = 'mfa_enabled',
  /** Account-level inactivity timeout turned on, possibly attributed back to a compliance notification (MOD-1618). */
  SessionTimeoutEnabled = 'session_timeout_enabled',
  IntroHeidi2 = 'intro_heidi_2',
}

export enum FieldType {
  Text = 'text',
  Dropdown = 'dropdown',
  Checkbox = 'checkbox',
  Button = 'button',
  Toggle = 'toggle',
  DragAndDrop = 'drag_and_drop',
  Click = 'click',
  Draw = 'draw',
  SignOff = 'sign_off',
}
